import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

const ThreatVisualization = () => {
  const containerRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [nodes, setNodes] = useState([]);
  const [containerReady, setContainerReady] = useState(false);
  const safeMouse = {
    x: Number.isFinite(mousePos.x) ? Math.max(0, Math.min(100, mousePos.x)) : 50,
    y: Number.isFinite(mousePos.y) ? Math.max(0, Math.min(100, mousePos.y)) : 50
  };
  const safeNodes = nodes
    .filter((node) => Number.isFinite(node?.x) && Number.isFinite(node?.y))
    .map((node) => ({
      ...node,
      x: Math.max(0, Math.min(100, node.x)),
      y: Math.max(0, Math.min(100, node.y))
    }));

  // Initialize nodes with velocity
  useEffect(() => {
    const initialNodes = Array.from({ length: 30 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      vx: (Math.random() - 0.5) * 0.05, // Very slow drift
      vy: (Math.random() - 0.5) * 0.05,
      size: Math.random() * 10 + 5,
    }));
    setNodes(initialNodes);
  }, []);

  // Animation loop for drifting nodes
  useEffect(() => {
    let animationFrameId;

    const animate = () => {
      setNodes(prevNodes => prevNodes.map(node => {
        const baseX = Number.isFinite(node.x) ? node.x : 50;
        const baseY = Number.isFinite(node.y) ? node.y : 50;
        const baseVx = Number.isFinite(node.vx) ? node.vx : (Math.random() - 0.5) * 0.05;
        const baseVy = Number.isFinite(node.vy) ? node.vy : (Math.random() - 0.5) * 0.05;

        let newX = baseX + baseVx;
        let newY = baseY + baseVy;
        let newVx = baseVx;
        let newVy = baseVy;

        // Bounce off walls
        if (newX <= 0 || newX >= 100) newVx *= -1;
        if (newY <= 0 || newY >= 100) newVy *= -1;

        return {
          ...node,
          x: newX,
          y: newY,
          vx: newVx,
          vy: newVy
        };
      }));
      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  const handleMouseMove = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    if (!containerReady) setContainerReady(true);
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePos({ x, y });
  };

  return (
    <div 
      className="threat-viz-container" 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: '700px', width: '100vw', marginLeft: 'calc(-50vw + 50%)' }}
    >
      {/* Background Animation Layer */}
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }}>
        {containerReady && safeNodes.length > 0 && (
        <svg className="viz-svg" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
          <defs>
            <radialGradient id="cursor-glow">
              <stop offset="0%" stopColor="rgba(88, 166, 255, 0.14)" />
              <stop offset="100%" stopColor="rgba(88, 166, 255, 0)" />
            </radialGradient>
          </defs>

          {/* Connections */}
          {safeNodes.map((node, i) => (
            safeNodes.slice(i + 1, i + 4).map((target, j) => {
              const dist = Math.sqrt(Math.pow(node.x - target.x, 2) + Math.pow(node.y - target.y, 2));
              if (dist > 30) return null; // Only connect close nodes
              
              return (
                <motion.line
                  key={`${i}-${j}`}
                  x1={node.x}
                  y1={node.y}
                  x2={target.x}
                  y2={target.y}
                  stroke="rgba(88, 166, 255, 0.16)"
                  strokeWidth="0.1"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 2, delay: Math.random() * 2 }}
                />
              );
            })
          ))}

          {/* Dynamic Connections to Cursor */}
          {safeNodes.map((node, i) => {
            const dist = Math.sqrt(Math.pow(node.x - safeMouse.x, 2) + Math.pow(node.y - safeMouse.y, 2));
            if (dist < 25) {
              return (
                <motion.line
                  key={`cursor-${i}`}
                  x1={safeMouse.x}
                  y1={safeMouse.y}
                  x2={node.x}
                  y2={node.y}
                  stroke="rgba(88, 166, 255, 0.34)"
                  strokeWidth="0.2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 - (dist / 25) }}
                />
              );
            }
            return null;
          })}
          
          {/* Ambient radar ping reinforcing real-time monitoring */}
          {[0, 2].map((delay) => (
            <motion.circle
              key={`ping-${delay}`}
              cx="50"
              cy="50"
              fill="none"
              stroke="rgba(35, 134, 54, 0.45)"
              strokeWidth="0.18"
              initial={{ r: 2, opacity: 0.55 }}
              animate={{ r: [2, 42], opacity: [0.55, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeOut', delay }}
            />
          ))}

          {/* Nodes */}
          {safeNodes.map((node) => {
            const isNearMouse = Math.abs(node.x - safeMouse.x) < 15 && Math.abs(node.y - safeMouse.y) < 15;
            return (
              <motion.circle
                key={node.id}
                cx={node.x}
                cy={node.y}
                r={node.size / 20}
                fill={isNearMouse ? "var(--gh-text)" : "var(--gh-link)"}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 0.4 }}
                animate={{ 
                  scale: isNearMouse ? 1.5 : [1, 1.2, 1],
                  opacity: isNearMouse ? 0.9 : [0.3, 0.5, 0.3]
                }}
                transition={{ duration: isNearMouse ? 0.2 : 1, repeat: isNearMouse ? 0 : Infinity, delay: Math.random() * 2 }}
              />
            );
          })}

          {/* Cursor Node (The "Hunter") */}
          <motion.circle
            cx={safeMouse.x}
            cy={safeMouse.y}
            r="1"
            fill="var(--gh-text)"
            animate={{ cx: safeMouse.x, cy: safeMouse.y }}
            transition={{ type: "spring", damping: 30, stiffness: 500 }}
          />
          
          {/* Cursor Ring */}
          <motion.circle
            cx={safeMouse.x}
            cy={safeMouse.y}
            r="6"
            fill="none"
            stroke="rgba(88, 166, 255, 0.72)"
            strokeWidth="0.3"
            strokeDasharray="2 1"
            animate={{ cx: safeMouse.x, cy: safeMouse.y, rotate: 360 }}
            transition={{ 
              cx: { type: "spring", damping: 30, stiffness: 500 },
              rotate: { duration: 4, repeat: Infinity, ease: "linear" }
            }}
          />
          
          {/* Cursor Glow */}
          <motion.circle
            cx={safeMouse.x}
            cy={safeMouse.y}
            r="20"
            fill="url(#cursor-glow)"
            animate={{ cx: safeMouse.x, cy: safeMouse.y }}
            transition={{ type: "spring", damping: 30, stiffness: 500 }}
          />

        </svg>
        )}
      </div>

      {/* Content Overlay */}
      <div className="threat-viz-content" style={{ position: 'relative', zIndex: 1, pointerEvents: 'none', background: 'radial-gradient(circle at center, rgba(13,17,23,0.88) 0%, rgba(13,17,23,0.55) 100%)', padding: '40px' }}>
        <h2 className="section-title">Real-Time Threat Monitoring</h2>
        <p className="section-subtitle">Continuous visibility into data flows, exposed assets, and suspicious behavior.</p>
        
        <p className="viz-description" style={{ 
          maxWidth: '700px', 
          textAlign: 'center', 
          marginBottom: '30px', 
          color: 'var(--gh-text-muted)',
          lineHeight: '1.6',
          background: 'rgba(13, 17, 23, 0.72)',
          padding: '20px',
          border: '1px solid var(--gh-border)',
          backdropFilter: 'blur(4px)'
        }}>
          PassiveGuard helps teams correlate code, infrastructure, and network signals in one place, making anomalous activity easier to detect, investigate, and prioritize.
        </p>

        <div className="viz-overlay" style={{ position: 'relative', bottom: 'auto', left: 'auto', marginTop: '20px', justifyContent: 'center' }}>
          <div className="stat-box">
            <span className="stat-label">THREATS BLOCKED</span>
            <span className="stat-value text-accent-danger">14,203</span>
          </div>
          <div className="stat-box">
            <span className="stat-label">ACTIVE NODES</span>
            <span className="stat-value text-accent-link">842</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreatVisualization;
